import { EMPTY_ARR } from '@dadohr/shared/domainTypes/emptyConstants';
import {
  IEmployeeAppInboxMessage,
  IEmployeeAppInboxResponse,
  IEmployeeAppInboxTask,
  IEmployeeAppPinnedItemsResponse,
  TaskStatus
} from '@dadohr/shared/domainTypes/experience';
import { orderBy } from 'lodash';
import { useUnsafeTranslation } from '../../../../../../i18next';
import { toFullName } from '../../../../../../services/user';
import { ROUTES_V2 } from '../../../../../routes';
import { InboxNavItemKey } from '../../components/Nav/InboxNav';
import {
  getInboxMessagesUrl,
  getInboxMessageUrl,
  getInboxTasksUrl,
  getInboxTaskUrl
} from '../../services/routes';
import { sortMessageGroups } from './components/MessageGroupTable';
import { sortMoments } from './components/MomentsTable';
import { sortTaskGroups } from './components/TaskGroupTable';
import { useGroupedInboxData } from './groups';

export const sortTasks = (
  tasks: IEmployeeAppInboxTask[],
  sort: 'deadline' | 'name' | 'completion'
) => {
  return orderBy(
    tasks,
    sort === 'deadline'
      ? [
          (tsk) => tsk.timing.end.toMillis(),
          (tsk) => toFullName(tsk.participant)
        ]
      : sort === 'completion'
      ? [
          (tsk) => tsk.completedAt?.toMillis() || Infinity,
          (tsk) => toFullName(tsk.participant)
        ]
      : [
          (tsk) => toFullName(tsk.participant),
          (tsk) => tsk.timing.end.toMillis()
        ]
  );
};

export const sortMessages = (
  messages: IEmployeeAppInboxMessage[],
  sort: 'sentAt' | 'name'
) => {
  return orderBy(
    messages,
    sort === 'sentAt'
      ? [(msg) => -msg.sendAt.toMillis(), (msg) => toFullName(msg.participant)]
      : [(msg) => toFullName(msg.participant), (msg) => -msg.sendAt.toMillis()]
  );
};

export type NextButtonDef = {
  label: string;
  variant?: 'outlined' | 'contained';
  preload?: {
    userAccessToken: string;
    instanceAccessToken: string;
  };
  url: string;
};

export const useNextButtonDefForInboxTask = (
  filteredData: IEmployeeAppInboxResponse,
  instanceAccessToken: string,
  taskTemplateId: string,
  selectedGroup: InboxNavItemKey | null,
  selectedUsers: string[] | null,
  selectedTemplates: Set<string>,
  pinnedItems: IEmployeeAppPinnedItemsResponse['items'],
  tableSortParam: string,
  groupSortParam: 'deadline' | 'name' | 'completion',
  returnTo: string
): NextButtonDef[] => {
  const { t } = useUnsafeTranslation();
  // get all tasks
  // group them all by table sort
  // find where we are.
  // if we're within a group, take the next person
  // otherwise take the next group or cycle through

  const { taskGroups: _taskGroups } = useGroupedInboxData(
    filteredData,
    pinnedItems,
    selectedGroup
  );

  const [tableSort = '', tableDir = 'asc'] = tableSortParam.split('---') as [
    string | undefined,
    'asc' | 'desc' | undefined
  ];

  const { userAccessToken } = filteredData;

  const taskGroups = sortTaskGroups(
    _taskGroups,
    tableSort,
    tableDir,
    selectedGroup
  );

  const currentGroupI = taskGroups.findIndex(
    (group) => group.taskTemplateId === taskTemplateId
  );

  const nextButtons: NextButtonDef[] = [];

  if (currentGroupI === -1) {
    return nextButtons;
  }
  const currentGroup = taskGroups[currentGroupI];

  // Inside batches we can opt out of this
  let tryToShowNextTaskButton = true;
  if (currentGroup.tasks.length > 1) {
    const sortedTasks = sortTasks(currentGroup.tasks, groupSortParam);
    const currentTaskI = sortedTasks.findIndex(
      (task) => task.instanceAccessToken === instanceAccessToken
    );
    if (currentTaskI !== -1) {
      // Always cycle around
      const nextTask = sortedTasks[currentTaskI + 1] || sortedTasks[0];
      if (nextTask) {
        nextButtons.push({
          label: `${t('Next person')}: ${toFullName(nextTask.participant)}`,
          variant:
            nextTask.status === TaskStatus.COMPLETE ? 'contained' : 'outlined',
          preload: {
            userAccessToken: userAccessToken,
            instanceAccessToken: nextTask.instanceAccessToken
          },
          url: getInboxTaskUrl(
            userAccessToken,
            nextTask.taskTemplateId,
            nextTask.instanceAccessToken,
            {
              returnTo,
              g: selectedGroup || undefined,
              u: selectedUsers,
              t: [...selectedTemplates],
              tsort: tableSortParam,
              sort: groupSortParam
            }
          )
        });
      }
      if (currentTaskI !== sortedTasks.length - 1) {
        tryToShowNextTaskButton = false;
      }
    }
  }

  if (!tryToShowNextTaskButton) {
    return nextButtons;
  }

  // If there is no next task in the current group, try to go to the next group.
  // Don't implement figuring out going to which instance - the next page can deal with that.
  // If there is no next group, cycle back to the first group.
  // If this lookup returns the same group as the current one, then we know
  // that there's only one group and no next button is needed.
  const nextGroup = taskGroups[currentGroupI + 1] || taskGroups[0];
  // If there is only one group altogether, show nothing
  if (!nextGroup || nextGroup === currentGroup) {
    return nextButtons;
  }
  nextButtons.push({
    label: `${t('Next task')}: ${
      nextGroup.tasks.length === 1
        ? nextGroup.tasks[0].taskTitleInterpolated
        : nextGroup.taskTitle
    }`,
    variant: nextGroup.tasks.every(
      (task) => task.status === TaskStatus.COMPLETE
    )
      ? 'contained'
      : 'outlined',
    preload: {
      userAccessToken: userAccessToken,
      instanceAccessToken: nextGroup.tasks[0].instanceAccessToken
    },
    url: getInboxTasksUrl(userAccessToken, nextGroup.taskTemplateId, {
      returnTo,
      g: selectedGroup || undefined,
      u: selectedUsers,
      t: [...selectedTemplates],
      tsort: tableSortParam,
      sort: groupSortParam
    })
  });
  return nextButtons;
};

export const useNextButtonDefForInboxMessage = (
  filteredData: IEmployeeAppInboxResponse,
  instanceAccessToken: string,
  messageTemplateId: string,
  selectedGroup: InboxNavItemKey | null,
  selectedUsers: string[] | null,
  selectedTemplates: Set<string>,
  pinnedItems: IEmployeeAppPinnedItemsResponse['items'],
  tableSortParam: string,
  groupSortParam: 'name' | 'sentAt',
  returnTo: string
): NextButtonDef[] => {
  const { t } = useUnsafeTranslation();
  // get all tasks
  // group them all by table sort
  // find where we are.
  // if we're within a group, take the next person
  // otherwise take the next group or cycle through

  const { messageGroups: _messageGroups } = useGroupedInboxData(
    filteredData,
    pinnedItems,
    selectedGroup
  );

  const [tableSort = '', tableDir = 'asc'] = tableSortParam.split('---') as [
    string | undefined,
    'asc' | 'desc' | undefined
  ];

  const { userAccessToken } = filteredData;

  const messageGroups = sortMessageGroups(_messageGroups, tableSort, tableDir);

  const nextButtons: NextButtonDef[] = [];

  const currentGroupI = messageGroups.findIndex(
    (group) => group.msgTemplateId === messageTemplateId
  );
  if (currentGroupI === -1) {
    return nextButtons;
  }
  const currentGroup = messageGroups[currentGroupI];

  // Inside batches we can opt out of this
  let tryToShowNextMessageButton = true;
  if (currentGroup.messages.length > 1) {
    const sortedMessages = sortMessages(currentGroup.messages, groupSortParam);
    const currentMessageI = sortedMessages.findIndex(
      (message) => message.instanceAccessToken === instanceAccessToken
    );
    if (currentMessageI !== -1) {
      // Always cycle around
      const nextMessage =
        sortedMessages[currentMessageI + 1] || sortedMessages[0];
      if (nextMessage) {
        nextButtons.push({
          label: `${t('Next person')}: ${toFullName(nextMessage.participant)}`,
          variant: 'outlined',
          preload: {
            userAccessToken: userAccessToken,
            instanceAccessToken: nextMessage.instanceAccessToken
          },
          url: getInboxMessageUrl(
            userAccessToken,
            nextMessage.msgTemplateId,
            nextMessage.instanceAccessToken,
            {
              returnTo,
              g: selectedGroup || undefined,
              u: selectedUsers,
              t: [...selectedTemplates],
              tsort: tableSortParam,
              sort: groupSortParam
            }
          )
        });
      }
      if (currentMessageI !== sortedMessages.length - 1) {
        tryToShowNextMessageButton = false;
      }
    }
  }

  if (!tryToShowNextMessageButton) {
    return nextButtons;
  }

  // If there is no next task in the current group, try to go to the next group.
  // Don't implement figuring out going to which instance - the next page can deal with that.
  // If there is no next group, cycle back to the first group.
  // If this lookup returns the same group as the current one, then we know
  // that there's only one group and no next button is needed.
  const nextGroup = messageGroups[currentGroupI + 1] || messageGroups[0];
  // If there is only one group altogether, show nothing
  if (!nextGroup || nextGroup === currentGroup) {
    return nextButtons;
  }
  nextButtons.push({
    label: `${t('Next message')}: ${
      nextGroup.messages.length === 1
        ? nextGroup.messages[0].msgSubjectInterpolated
        : nextGroup.msgTitle
    }`,
    variant: 'outlined',
    preload: {
      userAccessToken: userAccessToken,
      instanceAccessToken: nextGroup.messages[0].instanceAccessToken
    },
    url: getInboxMessagesUrl(userAccessToken, nextGroup.msgTemplateId, {
      returnTo,
      g: selectedGroup || undefined,
      u: selectedUsers,
      t: [...selectedTemplates],
      tsort: tableSortParam,
      sort: groupSortParam
    })
  });
  return nextButtons;
};

export const useNextButtonDefForInboxMoment = (
  filteredData: IEmployeeAppInboxResponse,
  momentInstanceAccessToken: string,
  selectedGroup: InboxNavItemKey | null,
  selectedUsers: string[] | null,
  selectedTemplates: Set<string>,
  pinnedItems: IEmployeeAppPinnedItemsResponse['items'],
  tableSortParam: string,
  returnTo: string,
  enabled: boolean
): NextButtonDef[] => {
  const { t } = useUnsafeTranslation();
  const { moments: _moments } = useGroupedInboxData(
    filteredData,
    pinnedItems,
    selectedGroup
  );

  if (!enabled) {
    return EMPTY_ARR;
  }

  const [tableSort = '', tableDir = 'asc'] = tableSortParam.split('---') as [
    string | undefined,
    'asc' | 'desc' | undefined
  ];

  const { userAccessToken } = filteredData;

  const nextButtons: NextButtonDef[] = [];
  const moments = sortMoments(_moments, tableSort, tableDir);

  const currentMomentI = moments.findIndex(
    (m) => m.momentAccessToken === momentInstanceAccessToken
  );
  if (currentMomentI === -1) {
    return nextButtons;
  }
  const nextMoment = moments[currentMomentI + 1] || moments[0];
  if (nextMoment) {
    nextButtons.push({
      label: `${t('Next news')}: ${nextMoment.templateTitle}`,
      variant: 'outlined',
      preload: {
        userAccessToken: userAccessToken,
        instanceAccessToken: nextMoment.momentAccessToken
      },
      url: ROUTES_V2.inboxMoment.url(
        userAccessToken,
        nextMoment.momentAccessToken,
        {
          returnTo,
          g: selectedGroup || undefined,
          u: selectedUsers,
          t: [...selectedTemplates],
          tsort: tableSortParam
        }
      )
    });
  }
  return nextButtons;
};
